import { createClient, type AuthChangeEvent, type Session } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL?.trim();
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY?.trim();

export const isSupabaseConfigured = Boolean(supabaseUrl && supabaseAnonKey);
export const supabase = isSupabaseConfigured ? createClient(supabaseUrl!, supabaseAnonKey!, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
}) : null;

export type AccessRequest = {
  firstNames: string;
  lastNames: string;
  age: number;
  country: string;
  city: string;
  email: string;
  password: string;
};

export type ApprovalStatus = "pending" | "approved" | "rejected" | "suspended";

export type TheraProfile = {
  id: string;
  first_names: string;
  last_names: string;
  age: number;
  country: string;
  city: string;
  email: string;
  role: "member" | "admin";
  status: ApprovalStatus;
  created_at: string;
};

export type PendingMember = Pick<
  TheraProfile,
  "id" | "first_names" | "last_names" | "age" | "country" | "city" | "email" | "created_at"
> & { email_confirmed: boolean };

export async function requestAccess(data: AccessRequest) {
  if (!supabase) return { error: new Error("Supabase todavía no está configurado.") };
  return supabase.auth.signUp({
    email: data.email,
    password: data.password,
    options: {
      emailRedirectTo: `${window.location.origin}/`,
      data: {
        first_names: data.firstNames,
        last_names: data.lastNames,
        age: data.age,
        country: data.country,
        city: data.city,
      },
    },
  });
}

export async function signIn(email: string, password: string) {
  if (!supabase) return { error: new Error("Supabase todavía no está configurado.") };
  return supabase.auth.signInWithPassword({ email, password });
}

export async function requestPasswordReset(email: string) {
  if (!supabase) return { error: new Error("Supabase todavía no está configurado.") };
  return supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/?password-recovery=1`,
  });
}

export async function updatePassword(password: string) {
  if (!supabase) return { error: new Error("Supabase todavía no está configurado.") };
  return supabase.auth.updateUser({ password });
}

export async function signOut() {
  if (!supabase) return;
  await supabase.auth.signOut();
}

export function observeSession(callback: (session: Session | null, event: AuthChangeEvent) => void) {
  if (!supabase) return () => undefined;
  supabase.auth.getSession().then(({ data }) => callback(data.session, "INITIAL_SESSION"));
  const { data } = supabase.auth.onAuthStateChange((event, session) => callback(session, event));
  return () => data.subscription.unsubscribe();
}

export async function getCurrentProfile(userId: string) {
  if (!supabase) throw new Error("Supabase no está configurado.");
  const { data, error } = await supabase
    .from("profiles")
    .select("id,first_names,last_names,age,country,city,email,role,status,created_at")
    .eq("id", userId)
    .maybeSingle();
  if (error) throw error;
  return data as TheraProfile | null;
}

export async function listPendingMembers() {
  if (!supabase) throw new Error("Supabase no está configurado.");
  const { data, error } = await supabase.rpc("admin_list_pending_profiles");
  if (error) throw error;
  return (data ?? []) as PendingMember[];
}

export async function reviewMember(
  memberId: string,
  decision: Extract<ApprovalStatus, "approved" | "rejected">,
) {
  if (!supabase) throw new Error("Supabase no está configurado.");
  const { error } = await supabase.rpc("admin_review_profile", {
    target_id: memberId,
    decision,
  });
  if (error) throw error;
}

export async function uploadWebpPhoto(input: { blob: Blob; routeId?: string; activityId?: string; altText?: string }) {
  if (!supabase) throw new Error("Supabase no está configurado.");
  const { data: authData } = await supabase.auth.getUser();
  const user = authData.user;
  if (!user) throw new Error("Inicia sesión para subir fotografías.");
  const parentId = input.routeId ?? input.activityId;
  if (!parentId) throw new Error("La fotografía necesita una ruta o actividad.");
  const objectPath = `${user.id}/${parentId}/${crypto.randomUUID()}.webp`;
  const upload = await supabase.storage.from("thera-photos").upload(objectPath, input.blob, {
    contentType: "image/webp",
    cacheControl: "31536000",
    upsert: false,
  });
  if (upload.error) throw upload.error;
  const metadata = await supabase.from("photos").insert({
    owner_id: user.id,
    route_id: input.routeId ?? null,
    activity_id: input.activityId ?? null,
    object_path: objectPath,
    mime_type: "image/webp",
    size_bytes: input.blob.size,
    alt_text: input.altText ?? "",
    status: "draft",
  });
  if (metadata.error) {
    await supabase.storage.from("thera-photos").remove([objectPath]);
    throw metadata.error;
  }
  return objectPath;
}

export async function getAccessToken() {
  if (!supabase) return null;
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}
